import React, { useState, useEffect, useCallback } from 'react';
import valorService from '../services/valor.service';
import { useAuth } from '../context/AuthContext';

const COR = {
  azul:     '#003399',
  borda:    '#e8eaed',
  texto:    '#1a1a2e',
  muted:    '#888',
  branco:   '#fff',
  verde:    '#1D9E75',
  verdeBg:  '#f0fff4',
  vermelho: '#e53935',
  vermBg:   '#fff0f0',
};

const TIPOS = {
  E: ['Imposto', 'Custo Oper.'],
  C: ['Vlr. Hora', 'Vlr. Fixo', 'Comissao'],
  // r139: + Vlr. Mensal SD (R$) e Acrescimo (%)
  L: ['Vlr. Hora Projeto', 'Vlr. Hora Sustentacao', 'Vlr. Hora Pontual', 'Vlr. Mensal SD', 'Desconto', 'Acrescimo'],
};

// r139: Acrescimo é % (igual Desconto)
const TIPOS_PCT = ['Imposto', 'Custo Oper.', 'Comissao', 'Desconto', 'Acrescimo'];

function formatarValor(tipo, valor) {
  if (valor === null || valor === undefined || valor === '') return '—';
  const n = parseFloat(valor);
  if (TIPOS_PCT.includes(tipo)) return `${n.toFixed(2)}%`;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatarData(data) {
  if (!data) return '—';
  // Pega apenas a parte da data (YYYY-MM-DD) e formata sem fuso
  const s = typeof data === 'string' ? data.slice(0, 10) : data;
  const [y, m, d] = s.split('-');
  if (!y || !m || !d) return '—';
  return `${d}/${m}/${y}`;
}

function calcularVigentes(valores) {
  const mapa = {};
  valores.forEach(v => {
    if (!mapa[v.tipo] || new Date(v.vigencia) > new Date(mapa[v.tipo])) {
      mapa[v.tipo] = v.vigencia;
    }
  });
  return mapa;
}

// ─── Modal de inclusão / edição ───────────────────────────────────────────────
function ModalValor({ aberto, modo, valor, vinculo, idVinculo, nomeVinculo, onFechar, onSalvo }) {
  const { usuario } = useAuth();
  const [form, setForm]         = useState({ tipo: '', vigencia: '', valor: '', descricao: '' });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro]         = useState('');

  useEffect(() => {
    if (!aberto) return;
    if (modo === 'editar' && valor) {
      setForm({ tipo: valor.tipo, vigencia: valor.vigencia ? valor.vigencia.slice(0, 10) : '', valor: valor.valor, descricao: valor.descricao || '' });
    } else {
      setForm({ tipo: TIPOS[vinculo]?.[0] || '', vigencia: '', valor: '', descricao: '' });
    }
    setErro('');
  }, [aberto, modo, valor, vinculo]);

  const handleCampo = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const salvar = async () => {
    setErro('');
    if (!form.tipo)             return setErro('Tipo é obrigatório.');
    if (!form.vigencia)         return setErro('Data de vigência é obrigatória.');
    if (!form.valor)            return setErro('Valor é obrigatório.');
    if (isNaN(parseFloat(form.valor))) return setErro('Valor inválido.');
    if (!form.descricao.trim()) return setErro('Descrição é obrigatória.');
    try {
      setSalvando(true);
      if (modo === 'novo') {
        await valorService.incluir({ vinculo, tipo: form.tipo, id_vinculo: idVinculo, vigencia: form.vigencia, valor: parseFloat(form.valor), descricao: form.descricao, usuario_id: usuario?.id || null });
      } else {
        await valorService.alterar(valor.id, { vigencia: form.vigencia, valor: parseFloat(form.valor), descricao: form.descricao });
      }
      onSalvo(); onFechar();
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Erro ao salvar valor.');
    } finally { setSalvando(false); }
  };

  if (!aberto) return null;

  const inputStyle = { width: '100%', height: '34px', padding: '0 10px', border: `1px solid ${COR.borda}`, borderRadius: '5px', fontSize: '13px', boxSizing: 'border-box', backgroundColor: COR.branco, color: COR.texto, outline: 'none', fontFamily: 'inherit' };
  const isPct = TIPOS_PCT.includes(form.tipo);

  return (
    <div onClick={onFechar} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '16px', fontFamily: "'Manrope', sans-serif" }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: COR.branco, borderRadius: '8px', width: '100%', maxWidth: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
        <div style={{ backgroundColor: COR.azul, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#fff' }}>{modo === 'novo' ? 'Novo Valor' : 'Alterar Valor'} — {nomeVinculo}</span>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '16px' }}>
          {erro && <div style={{ backgroundColor: COR.vermBg, border: '1px solid #ffcccc', borderRadius: '5px', color: COR.vermelho, fontSize: '13px', padding: '8px 12px', marginBottom: '12px' }}>{erro}</div>}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: COR.muted, marginBottom: '4px' }}>Tipo <span style={{ color: COR.vermelho }}>*</span></label>
            <select name="tipo" value={form.tipo} onChange={handleCampo} disabled={modo === 'editar'} style={{ ...inputStyle, cursor: modo === 'editar' ? 'default' : 'pointer', backgroundColor: modo === 'editar' ? '#fafafa' : COR.branco }}>
              {(TIPOS[vinculo] || []).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: COR.muted, marginBottom: '4px' }}>Vigência <span style={{ color: COR.vermelho }}>*</span></label>
              <input name="vigencia" type="date" value={form.vigencia} onChange={handleCampo} style={inputStyle} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: COR.muted, marginBottom: '4px' }}>{isPct ? 'Percentual (%)' : 'Valor (R$)'} <span style={{ color: COR.vermelho }}>*</span></label>
              <input name="valor" type="number" step="0.01" value={form.valor} onChange={handleCampo} style={inputStyle} placeholder="0,00" />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: COR.muted, marginBottom: '4px' }}>Descrição <span style={{ color: COR.vermelho }}>*</span></label>
            <input name="descricao" value={form.descricao} onChange={handleCampo} style={inputStyle} placeholder="Motivo da alteração..." maxLength={120} />
          </div>
        </div>
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${COR.borda}`, display: 'flex', gap: '8px', justifyContent: 'flex-end', backgroundColor: '#fafafa' }}>
          <button onClick={onFechar} style={{ padding: '7px 16px', border: `1px solid ${COR.borda}`, borderRadius: '5px', backgroundColor: 'transparent', color: COR.texto, fontSize: '13px', cursor: 'pointer' }}>Cancelar</button>
          <button onClick={salvar} disabled={salvando} style={{ padding: '7px 18px', border: 'none', borderRadius: '5px', backgroundColor: COR.azul, color: '#fff', fontSize: '13px', fontWeight: '600', cursor: salvando ? 'default' : 'pointer', opacity: salvando ? 0.7 : 1 }}>{salvando ? 'Salvando...' : 'Confirmar'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
function GridValores({ vinculo, idVinculo, nome, somenteLeitura, onAtualizado }) {
  const [valores, setValores]           = useState([]);
  const [carregando, setCarregando]     = useState(false);
  const [modalAberto, setModalAberto]   = useState(false);
  const [modoModal, setModoModal]       = useState('novo');
  const [valorSel, setValorSel]         = useState(null);
  const [confirmExclusao, setConfirmExclusao] = useState(null);
  const [filtroTipo, setFiltroTipo]         = useState('');

  const carregar = useCallback(async () => {
    if (!idVinculo) return;
    try { setCarregando(true); setValores(await valorService.listar(vinculo, idVinculo)); }
    catch { setValores([]); }
    finally { setCarregando(false); }
  }, [vinculo, idVinculo]);

  useEffect(() => { carregar(); }, [carregar]);

  const handleSalvo = () => { carregar(); if (onAtualizado) onAtualizado(); };
  const abrirNovo   = () => { setValorSel(null);  setModoModal('novo');   setModalAberto(true); };
  const abrirEditar = (v) => { setValorSel(v);    setModoModal('editar'); setModalAberto(true); };

  const excluir = async (v) => {
    try { await valorService.excluir(v.id); setConfirmExclusao(null); handleSalvo(); }
    catch { alert('Erro ao excluir valor.'); }
  };

  const valoresFiltrados = filtroTipo ? valores.filter(v => v.tipo === filtroTipo) : valores;
  const vigentes = calcularVigentes(valores); // usa lista completa para calcular vigência corretamente
  const ehVigente = (v) => vigentes[v.tipo] === v.vigencia;

  const btnAcaoStyle = (cor) => ({
    width: '26px', height: '26px', border: `1px solid ${COR.borda}`,
    borderRadius: '4px', backgroundColor: 'transparent', color: cor,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  });

  return (
    <div style={{ marginTop: '8px' }}>

      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '11px', fontWeight: '700', color: COR.azul, textTransform: 'uppercase', letterSpacing: '0.6px', marginRight: 'auto' }}>Histórico de Valores</span>
        <select
          value={filtroTipo}
          onChange={e => setFiltroTipo(e.target.value)}
          style={{ height: '28px', padding: '0 8px', border: `1px solid ${COR.borda}`, borderRadius: '5px', fontSize: '12px', backgroundColor: COR.branco, color: COR.texto, cursor: 'pointer', outline: 'none' }}
        >
          <option value="">Todos os tipos</option>
          {(TIPOS[vinculo] || []).map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {!somenteLeitura && (
          <button onClick={abrirNovo} disabled={!idVinculo} style={{ padding: '4px 12px', border: `1px solid ${COR.azul}`, borderRadius: '5px', backgroundColor: 'transparent', color: COR.azul, fontSize: '12px', fontWeight: '600', cursor: idVinculo ? 'pointer' : 'default', opacity: idVinculo ? 1 : 0.4 }}>
            + Novo Valor
          </button>
        )}
      </div>

      {/* Legenda */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '8px' }}>
        {[['#1D9E75', 'Vigente'], ['#e53935', 'Fora de vigência']].map(([cor, label]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: COR.muted }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: cor, display: 'inline-block' }} />
            {label}
          </div>
        ))}
      </div>

      {/* Tabela */}
      <div style={{ border: `1px solid ${COR.borda}`, borderRadius: '6px', overflow: 'hidden', fontSize: '12px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#fafafa', borderBottom: `1px solid ${COR.borda}` }}>
              {['Tipo', 'Vigência', 'Valor', 'Descrição', 'Usuário', ...(somenteLeitura ? [] : [''])].map((h, i) => (
                <th key={i} style={{ padding: '7px 10px', textAlign: 'left', fontSize: '10px', fontWeight: '600', color: COR.muted, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {carregando ? (
              <tr><td colSpan="6" style={{ padding: '20px', textAlign: 'center', color: COR.muted }}>Carregando...</td></tr>
            ) : valoresFiltrados.length === 0 ? (
              <tr><td colSpan="6" style={{ padding: '20px', textAlign: 'center', color: COR.muted }}>Nenhum valor cadastrado.</td></tr>
            ) : valoresFiltrados.map((v, i) => {
              const vig = ehVigente(v);
              return (
                <tr key={v.id} style={{ borderBottom: i < valores.length - 1 ? `1px solid ${COR.borda}` : 'none', backgroundColor: vig ? COR.verdeBg : COR.vermBg }}>
                  <td style={{ padding: '7px 10px', fontWeight: '600', color: COR.texto, whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: vig ? COR.verde : COR.vermelho, flexShrink: 0, display: 'inline-block' }} />
                      {v.tipo}
                    </div>
                  </td>
                  <td style={{ padding: '7px 10px', color: COR.muted, whiteSpace: 'nowrap' }}>{formatarData(v.vigencia)}</td>
                  <td style={{ padding: '7px 10px', fontWeight: vig ? '600' : '400', color: vig ? COR.verde : COR.vermelho, whiteSpace: 'nowrap' }}>{formatarValor(v.tipo, v.valor)}</td>
                  <td style={{ padding: '7px 10px', color: COR.muted }}>{v.descricao}</td>
                  <td style={{ padding: '7px 10px', color: COR.muted, whiteSpace: 'nowrap' }}>{v.usuario_nome || '—'}</td>
                  {!somenteLeitura && (
                    <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>
                      {confirmExclusao === v.id ? (
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <span style={{ fontSize: '11px', color: COR.vermelho }}>Excluir?</span>
                          <button onClick={() => excluir(v)} style={{ padding: '2px 8px', border: 'none', borderRadius: '4px', backgroundColor: COR.vermelho, color: '#fff', fontSize: '11px', cursor: 'pointer' }}>Sim</button>
                          <button onClick={() => setConfirmExclusao(null)} style={{ padding: '2px 8px', border: `1px solid ${COR.borda}`, borderRadius: '4px', backgroundColor: 'transparent', color: COR.muted, fontSize: '11px', cursor: 'pointer' }}>Não</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={() => abrirEditar(v)} title="Alterar" style={btnAcaoStyle(COR.azul)}>
                            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M11.5 2.5l2 2L5 13H3v-2L11.5 2.5z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </button>
                          <button onClick={() => setConfirmExclusao(v.id)} title="Excluir" style={btnAcaoStyle(COR.vermelho)}>
                            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M3 4h10M6 4V3h4v1M5 4l.5 8h5L11 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ModalValor aberto={modalAberto} modo={modoModal} valor={valorSel} vinculo={vinculo} idVinculo={idVinculo} nomeVinculo={nome} onFechar={() => setModalAberto(false)} onSalvo={handleSalvo} />
    </div>
  );
}

export default GridValores;
